import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ConfigurationFreshnessBanner } from '@/components/ConfigurationFreshnessBanner'
import { OnboardingBanner } from '@/components/OnboardingBanner'
import { usePushGatewayCapabilities } from '@/hooks/usePushGateway'
import { configurationNavigationSections, type NavigationSection } from '@/navigation/configuration-navigation'
import {
  ArrowRightEndOnRectangleIcon,
  HomeIcon,
  ShieldCheckIcon,
  CogIcon,
  ServerIcon,
  GlobeAltIcon,
  AdjustmentsHorizontalIcon,
  DocumentDuplicateIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'

const sectionIcons: Record<string, typeof ServerIcon> = {
  'start': HomeIcon,
  'foundation-engine': ServerIcon,
  'local-sip-media': GlobeAltIcon,
  'profiles': AdjustmentsHorizontalIcon,
  'peers': GlobeAltIcon,
  'authentication': ShieldCheckIcon,
  'manipulation': DocumentDuplicateIcon,
  'routing': AdjustmentsHorizontalIcon,
  'observability': ServerIcon,
  'utilities': WrenchScrewdriverIcon,
}

export default function IndustryStandardLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const pushGatewayCapabilities = usePushGatewayCapabilities()
  const showPushGateway = pushGatewayCapabilities.data?.data.configured === true

  const navigation = configurationNavigationSections.map((section: NavigationSection) => ({
    name: section.label,
    icon: sectionIcons[section.key] ?? ServerIcon,
      children: section.items
      .filter((item) => (item.path !== '/system/push-gateway' || showPushGateway) && (item.path !== '/system/ongoing-calls' || user?.role === 'admin') && (item.path !== '/system/sip-traffic-logs' || user?.role === 'admin') && (item.path !== '/system/logs' || user?.role === 'admin'))
      .map((item) => ({
        name: item.label,
        href: item.path,
        current: item.path === '/system/containers'
          ? location.pathname.startsWith('/system/containers')
          : location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/'),
      })),
  }))

  const isChildActive = (children: Array<{ current?: boolean }>) => {
    return children.some(child => child.current)
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col">
        <div className="flex h-16 items-center justify-center border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <svg
              className="h-9 w-9"
              viewBox="0 0 64 64"
              aria-hidden="true"
            >
              <circle cx="21" cy="32" r="18" fill="none" stroke="#2563EB" strokeWidth="2.5" />
              <circle cx="43" cy="32" r="18" fill="none" stroke="#06B6D4" strokeWidth="2.5" />
            </svg>
            <div>
              <h1 className="text-xl font-extrabold tracking-tight leading-none">
                <span className="text-slate-50">sbc</span>
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">lite</span>
              </h1>
              <p className="mt-0.5 text-[10px] tracking-[0.18em] uppercase text-slate-400">The SBC, simplified</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto mt-5 space-y-1 px-2 pb-4">
          {navigation.map((item) => (
            <div key={item.name}>
              <div className="space-y-1">
                <div className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isChildActive(item.children)
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
                }`}>
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </div>
                <div className="ml-8 space-y-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        child.current
                          ? 'bg-slate-800 text-white'
                          : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
                      }`}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </nav>
        
        <div className="border-t border-slate-800 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Link 
              to="/settings/user" 
              className="flex items-center min-w-0 group rounded-lg p-1 -m-1 hover:bg-slate-800 transition-colors"
              title="User Preferences"
            >
              <div className="relative flex-shrink-0">
                <UserCircleIcon className="h-9 w-9 text-slate-400 group-hover:text-cyan-300 transition-colors" />
                <CogIcon className="absolute -bottom-0.5 -right-0.5 h-4 w-4 text-slate-500 group-hover:text-cyan-300 transition-colors bg-slate-900 rounded-full p-0.5" />
              </div>
              <div className="ml-3 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-white truncate group-hover:text-cyan-300 transition-colors">{user?.name}</p>
                  {user?.role === 'admin' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">
                      Admin
                    </span>
                  )}
                </div>
                <p className="text-xs font-medium text-slate-400 truncate">{user?.email}</p>
              </div>
            </Link>
            <button
              onClick={logout}
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800/60 hover:text-white flex-shrink-0 transition-colors"
              title="Logout"
            >
              <ArrowRightEndOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="pl-64">
        <ConfigurationFreshnessBanner />
        <OnboardingBanner />
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
